import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';

interface TxDisplay {
  id: number;
  date: string;
  description: string;
  amount: number;
  category: string;
  account: string;
  status: 'cleared' | 'pending' | 'reconciled';
  reference?: string;
}

export function Transactions() {
  const [transactions, setTransactions] = useState<TxDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { idToken } = useCognitoAuth();

  useEffect(() => {
    if (!idToken) return;
    async function fetchTransactions() {
      try {
        const data = await api.get<{ transactions: { id: number; date: string; description: string; amount: string | number; type: string; account_id: number; reconciled: boolean }[] }>('/api/transactions', { errorCode: ErrorCode.FETCH_TRANSACTIONS, token: idToken! });
        setTransactions((data.transactions ?? []).map(t => ({
          id: t.id,
          date: t.date,
          description: t.description,
          amount: Number(t.amount),
          category: t.type,
          account: String(t.account_id ?? ''),
          status: t.reconciled ? 'reconciled' : 'cleared' as 'cleared' | 'pending' | 'reconciled',
          reference: undefined,
        })));
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch transactions');
      } finally {
        setLoading(false);
      }
    }
    void fetchTransactions();
  }, [idToken]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-CA');
  };

  const getStatusVariant = (status: TxDisplay['status']): 'secondary' | 'outline' | 'default' => {
    switch (status) {
      case 'reconciled': return 'secondary';
      case 'cleared': return 'secondary';
      case 'pending': return 'outline';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <i className="bi bi-exclamation-triangle text-3xl text-red-600 mb-4"></i>
        <p className="text-muted-foreground">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Transactions</h1>
          <p className="text-sm text-muted-foreground">Bank transactions imported from connected accounts</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm">
            <i className="bi bi-funnel"></i> Filter
          </Button>
          <Button variant="outline" size="sm">
            <i className="bi bi-download"></i> Export
          </Button>
          <Button variant="default" size="sm">
            <i className="bi bi-plus"></i> Add Transaction
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">Date Range</label>
              <div className="flex items-center gap-2">
                <input type="date" className="px-3 py-2 text-sm border border-input rounded-md bg-background" defaultValue="2026-03-01" />
                <span className="text-muted-foreground">to</span>
                <input type="date" className="px-3 py-2 text-sm border border-input rounded-md bg-background" defaultValue="2026-04-02" />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">Account</label>
              <select className="px-3 py-2 text-sm border border-input rounded-md bg-background">
                <option>All Accounts</option>
                <option>Operating Bank Account</option>
                <option>Savings Account</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">Status</label>
              <select className="px-3 py-2 text-sm border border-input rounded-md bg-background">
                <option>All Statuses</option>
                <option>Reconciled</option>
                <option>Cleared</option>
                <option>Pending</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle>Transactions ({transactions.length})</CardTitle>
            <div className="flex gap-4 text-sm">
              <span className="flex items-center gap-1">
                <i className="bi bi-arrow-up text-green-600"></i>
                {formatCurrency(transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0))}
              </span>
              <span className="flex items-center gap-1">
                <i className="bi bi-arrow-down text-red-600"></i>
                {formatCurrency(transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0))}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Date</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Description</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Category</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Account</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Status</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Amount</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4 font-mono text-sm">{formatDate(tx.date)}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        {tx.description}
                        {tx.reference && (
                          <span className="text-xs text-muted-foreground">Ref: {tx.reference}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">{tx.category}</td>
                    <td className="py-3 px-4">{tx.account}</td>
                    <td className="py-3 px-4">
                      <Badge variant={getStatusVariant(tx.status)}>
                        {tx.status}
                      </Badge>
                    </td>
                    <td className={`py-3 px-4 text-right font-mono ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount >= 0 ? '+' : '-'}{formatCurrency(tx.amount)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm">
                          <i className="bi bi-eye"></i>
                        </Button>
                        <Button variant="ghost" size="sm">
                          <i className="bi bi-pencil"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <Button variant="outline" size="sm" disabled>
          <i className="bi bi-chevron-left"></i> Previous
        </Button>
        <span className="text-sm text-muted-foreground">Page 1 of 1</span>
        <Button variant="outline" size="sm" disabled>
          Next <i className="bi bi-chevron-right"></i>
        </Button>
      </div>
    </div>
  );
}
